import React, { useEffect, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useNotification } from "@context/NotificationContext";
import {
  BAY_HIRE_PORTS,
  BAY_PREFERENCES,
  PAYMENT_TYPES,
  type BayHirePort,
  type BayPreference,
  type PaymentType,
} from "@features/bay-hire/constants";
import {
  useResubmitBayHireRequest,
  useStartBayHireRequest,
  useUpdateBayHireRequest,
  useUploadBayHireAttachmentMetadata,
} from "@features/bay-hire/hooks/useBayHireMutations";
import { useBayHireRequestById } from "@features/bay-hire/hooks/useBayHireQueries";
import { useBayHireRequestFormStore } from "@features/bay-hire/store/useBayHireRequestForm.store";
import type { BayHireRequest } from "@features/bay-hire/types";
import { findLatestAttachmentByType } from "@features/bay-hire/utils/bayHireAttachments";
import { bayAssignmentsInitialValue } from "@features/bay-hire/utils/bayHireFormPrefill";
import {
  canRenewBayHireRequest,
  canResubmitBayHireRequest,
  canUpdateBayHireRequest,
} from "@features/bay-hire/utils/bayHireRequestActionGuards";
import { getApiErrorDetail } from "@features/bay-hire/utils/getApiErrorDetail";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import BayHireFormPage from "./BayHireFormPage";
import BayHireRequestForm, { type BayHireRequestFormData } from "./BayHireRequestForm";

const BayHireRequestFormPage: React.FC = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const stateRequest = (location.state as { request?: BayHireRequest } | null)?.request;

  // DFT-0096: no `:id` route param - which existing request (if any) this form
  // acts on comes from `location.state` (the fast path, present when navigating
  // fresh from the detail page/list) or, failing that, the persisted
  // `requestId` (the refresh-survival fallback - `location.state` doesn't
  // survive a refresh, but the Zustand `persist` store does). See
  // frontend-guidelines.md Section 1's carve-out for the full rationale and its
  // accepted tradeoff.
  const storeRequestId = useBayHireRequestFormStore((state) => state.requestId);
  const setRequestId = useBayHireRequestFormStore((state) => state.setRequestId);
  const resetPersistedForm = useBayHireRequestFormStore((state) => state.resetForm);

  // Leak-guard (mirrors AddMovementService.tsx's vesselHasChanged): if
  // location.state names a DIFFERENT request than the one currently
  // persisted, the old draft belongs to a request we're no longer looking at
  // - purge it before it can leak into this one. Not needed for "+ Bay Hire"
  // (create): that button already clears the store itself before navigating
  // (useBayHireContainer.tsx), so storeRequestId is already null by the time
  // this component mounts for a fresh create.
  useEffect(() => {
    if (stateRequest && stateRequest.id !== storeRequestId) {
      resetPersistedForm();
      // DFT-0106: resetPersistedForm() only ever clears useBayHireRequestFormStore - it has no
      // knowledge of useUploadStore, so an HOD document staged against the PREVIOUS request
      // would otherwise silently leak into this one (dropzone disabled, no visible Remove UI,
      // since the form's own `value` genuinely resets but the upload-store entry doesn't).
      // HOD_APPROVAL is the only key BayHireCreateAttachmentUpload (this form's one upload spot)
      // ever writes to.
      useUploadStore.getState().clearFilesForKey("HOD_APPROVAL");
      setRequestId(stateRequest.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateRequest?.id]);

  const id = stateRequest?.id ?? storeRequestId ?? undefined;
  const { data: request, isLoading, isError } = useBayHireRequestById(id ?? null, stateRequest);

  const startMutation = useStartBayHireRequest();
  const resubmitMutation = useResubmitBayHireRequest();
  const updateMutation = useUpdateBayHireRequest();
  const registerHodAttachmentMutation = useUploadBayHireAttachmentMetadata();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // DFT-0096: also clears the persisted draft (form field values + requestId)
  // - called on Cancel (every variant's onCancel) and after every successful
  // submit, so the next time the form is opened it starts clean rather than
  // resuming this request's now-finished/abandoned edit.
  const goToGrid = () => {
    resetPersistedForm();
    // DFT-0106: same gap as the leak-guard effect above - resetPersistedForm() doesn't know
    // about useUploadStore, so a staged HOD document would leak into the next Create/Update/
    // Resubmit/Renew session (the single most common path of all: submit successfully, then
    // "+ Bay Hire" or open another request - or Cancel, since goToGrid is called on both).
    useUploadStore.getState().clearFilesForKey("HOD_APPROVAL");
    navigate(`/${paths.bayHire.index}`);
  };

  if (id) {
    if (isLoading) {
      return (
        <div className="relative text-center py-8">
          <LoadingOverlay loading />
        </div>
      );
    }
    if (isError || !request) {
      return <Navigate to={`/${paths.bayHire.index}`} replace />;
    }
  }

  const mode = !id
    ? "create"
    : request && canResubmitBayHireRequest(request)
      ? "resubmit"
      : request && canUpdateBayHireRequest(request)
        ? "update"
        : request && canRenewBayHireRequest(request)
          ? "renew"
          : null;

  if (id && (mode === null || !request)) {
    return <Navigate to={`/${paths.bayHire.index}`} replace />;
  }

  const validateFormData = (formData: BayHireRequestFormData) => {
    if (!formData.port || !(BAY_HIRE_PORTS as readonly string[]).includes(formData.port)) {
      setSubmitError(t("bay-hire.invalid-port"));
      return false;
    }
    if (
      !formData.bayPreference ||
      !(BAY_PREFERENCES as readonly string[]).includes(formData.bayPreference)
    ) {
      setSubmitError(t("bay-hire.invalid-bay-preference"));
      return false;
    }
    if (
      !formData.paymentType ||
      !(PAYMENT_TYPES as readonly string[]).includes(formData.paymentType)
    ) {
      setSubmitError(t("bay-hire.invalid-payment-type"));
      return false;
    }
    if (!formData.bayAssignments || formData.bayAssignments.length === 0) {
      setSubmitError(t("bay-hire.no-bays-selected"));
      return false;
    }
    return true;
  };

  const handleCreateSubmit = async (formData: BayHireRequestFormData) => {
    setSubmitError(null);
    if (!validateFormData(formData)) return;

    try {
      const response = await startMutation.mutateAsync({
        port: formData.port as BayHirePort,
        bayPreference: formData.bayPreference as BayPreference,
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        paymentType: formData.paymentType as PaymentType,
        responsiblePersonName: formData.responsiblePersonName,
        responsiblePersonDesignation: formData.responsiblePersonDesignation,
        remarks: formData.remarks,
        bayAssignments: formData.bayAssignments.map((bay) => ({
          referenceDataBayId: bay.referenceDataBayId,
        })),
        attachments: formData.attachments?.length ? formData.attachments : undefined,
      });

      if (response.workflowStartStatus === "FAILED_TO_START") {
        notify({
          title: t("bay-hire.workflow-start-failed"),
          type: "warning",
        });
      } else {
        notify({
          title: t("bay-hire.submit-success"),
          type: "success",
        });
      }
      goToGrid();
    } catch (error) {
      console.error("Failed to create Bay Hire request:", error);
      // DFT-0077 (AD-5): prefer the backend's own explanation over the generic message.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.submit-error")));
    }
  };

  const handleRenewSubmit = async (formData: BayHireRequestFormData) => {
    setSubmitError(null);
    if (!validateFormData(formData)) return;

    try {
      const response = await startMutation.mutateAsync({
        port: formData.port as BayHirePort,
        bayPreference: formData.bayPreference as BayPreference,
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        paymentType: formData.paymentType as PaymentType,
        responsiblePersonName: formData.responsiblePersonName,
        responsiblePersonDesignation: formData.responsiblePersonDesignation,
        remarks: formData.remarks,
        bayAssignments: formData.bayAssignments.map((bay) => ({
          referenceDataBayId: bay.referenceDataBayId,
        })),
        attachments: formData.attachments?.length ? formData.attachments : undefined,
      });

      if (response.workflowStartStatus === "FAILED_TO_START") {
        notify({
          title: t("bay-hire.workflow-start-failed"),
          type: "warning",
        });
      } else {
        notify({
          title: t("bay-hire.renew-submit-success"),
          type: "success",
        });
      }
      goToGrid();
    } catch (error) {
      console.error("Failed to submit Bay Hire renewal:", error);
      // DFT-0077 (AD-5): prefer the backend's own explanation over the generic message.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.renew-submit-error")));
    }
  };

  const handleResubmitSubmit = async (formData: BayHireRequestFormData) => {
    if (!request) return;
    setSubmitError(null);

    const numberOfBays = formData.bayAssignments.length;
    const currentAttachment = formData.attachments?.[0];

    try {
      let hodAttachmentId: string | null;
      if (currentAttachment && !currentAttachment.id) {
        const registered = await registerHodAttachmentMutation.mutateAsync({
          id: request.id,
          request: {
            attachmentType: currentAttachment.attachmentType,
            documentUrl: currentAttachment.documentUrl,
            originalFileName: currentAttachment.originalFileName,
            contentType: currentAttachment.contentType,
            fileSizeBytes: currentAttachment.fileSizeBytes,
          },
        });
        hodAttachmentId = registered.id;
      } else {
        hodAttachmentId = currentAttachment?.id ?? null;
      }

      await resubmitMutation.mutateAsync({
        id: request.id,
        request: {
          dateFrom: formData.dateFrom,
          dateTo: formData.dateTo,
          // DFT-0087: paymentType is editable on Resubmit (unlike Update, where it's locked) -
          // this was never read into the submitted payload, so a vendor changing it here had the
          // change silently dropped, even though the API returned 202.
          paymentType: formData.paymentType as PaymentType,
          bayPreference: formData.bayPreference as BayPreference,
          numberOfBays,
          responsiblePersonName: formData.responsiblePersonName,
          responsiblePersonDesignation: formData.responsiblePersonDesignation,
          bayAssignments: formData.bayAssignments.map((bay) => ({
            referenceDataBayId: bay.referenceDataBayId,
          })),
          remarks: formData.remarks,
          hodAttachmentId,
        },
      });
      goToGrid();
    } catch (error) {
      console.error("Failed to resubmit Bay Hire request:", error);
      // DFT-0077 (AD-5): prefer the backend's own explanation over the generic message.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.resubmit-error")));
    }
  };

  const handleUpdateSubmit = async (formData: BayHireRequestFormData) => {
    if (!request) return;
    setSubmitError(null);

    if (!formData.bayAssignments || formData.bayAssignments.length === 0) {
      setSubmitError(t("bay-hire.no-bays-selected"));
      return;
    }

    const bayPreferenceChanged = formData.bayPreference !== request.bayPreference;
    const currentAttachment = formData.attachments?.[0];

    try {
      let hodAttachmentId: string | null;
      if (currentAttachment && !currentAttachment.id) {
        const registered = await registerHodAttachmentMutation.mutateAsync({
          id: request.id,
          request: {
            attachmentType: currentAttachment.attachmentType,
            documentUrl: currentAttachment.documentUrl,
            originalFileName: currentAttachment.originalFileName,
            contentType: currentAttachment.contentType,
            fileSizeBytes: currentAttachment.fileSizeBytes,
          },
        });
        hodAttachmentId = registered.id;
      } else {
        hodAttachmentId = currentAttachment?.id ?? null;
      }

      await updateMutation.mutateAsync({
        id: request.id,
        request: {
          dateFrom: formData.dateFrom,
          dateTo: formData.dateTo,
          responsiblePersonName: formData.responsiblePersonName,
          responsiblePersonDesignation: formData.responsiblePersonDesignation,
          ...(bayPreferenceChanged && { bayPreference: formData.bayPreference as BayPreference }),
          bayAssignments: formData.bayAssignments.map((bay) => ({
            referenceDataBayId: bay.referenceDataBayId,
          })),
          remarks: formData.remarks,
          hodAttachmentId,
        },
      });
      goToGrid();
    } catch (error) {
      console.error("Failed to update Bay Hire request:", error);
      // DFT-0077 (AD-5): prefer the backend's own explanation over the generic message.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.update-error")));
    }
  };

  const latestHodAttachment = request
    ? findLatestAttachmentByType(request.attachments, "HOD_APPROVAL")
    : null;
  const hodAttachmentInitialValue = latestHodAttachment
    ? [
        {
          id: latestHodAttachment.id,
          attachmentType: latestHodAttachment.attachmentType,
          documentUrl: latestHodAttachment.documentUrl,
          originalFileName: latestHodAttachment.originalFileName ?? undefined,
        },
      ]
    : [];

  if (mode === "update" && request) {
    return (
      <BayHireFormPage
        titleKey="bay-hire.update-form-title"
        titleDefault="Update Bay Hire"
        submitError={submitError}
      >
        <BayHireRequestForm
          onSubmit={handleUpdateSubmit}
          onCancel={goToGrid}
          submitLabel={t("bay-hire.update-form-submit")}
          lockImmutableFields
          requestId={request.id}
          initialValues={{
            port: request.port,
            bayPreference: request.bayPreference,
            dateFrom: request.dateFrom,
            dateTo: request.dateTo,
            paymentType: request.paymentType,
            responsiblePersonName: request.responsiblePersonName,
            responsiblePersonDesignation: request.responsiblePersonDesignation,
            bayAssignments: bayAssignmentsInitialValue(request),
            remarks: request.remarks ?? "",
            attachments: hodAttachmentInitialValue,
          }}
        />
      </BayHireFormPage>
    );
  }

  if (mode === "resubmit" && request) {
    return (
      <BayHireFormPage
        titleKey="bay-hire.request-form-title"
        titleDefault="Bay Hire Request"
        submitError={submitError}
      >
        <BayHireRequestForm
          onSubmit={handleResubmitSubmit}
          onCancel={goToGrid}
          submitLabel={t("bay-hire.resubmit")}
          lockBayPreferenceOnSelection={false}
          requestId={request.id}
          initialValues={{
            port: request.port,
            bayPreference: request.bayPreference,
            dateFrom: request.dateFrom,
            dateTo: request.dateTo,
            paymentType: request.paymentType,
            responsiblePersonName: request.responsiblePersonName,
            responsiblePersonDesignation: request.responsiblePersonDesignation,
            bayAssignments: bayAssignmentsInitialValue(request),
            remarks: request.remarks ?? "",
            attachments: hodAttachmentInitialValue,
          }}
        />
      </BayHireFormPage>
    );
  }

  if (mode === "renew" && request) {
    return (
      <BayHireFormPage
        titleKey="bay-hire.request-form-title"
        titleDefault="Bay Hire Request"
        submitError={submitError}
      >
        <BayHireRequestForm
          onSubmit={handleRenewSubmit}
          onCancel={goToGrid}
          submitLabel={t("bay-hire.renew-submit")}
          lockBayPreferenceOnSelection={false}
          initialValues={{
            port: request.port,
            bayPreference: request.bayPreference,
            dateFrom: request.dateFrom,
            dateTo: request.dateTo,
            paymentType: request.paymentType,
            responsiblePersonName: request.responsiblePersonName,
            responsiblePersonDesignation: request.responsiblePersonDesignation,
            bayAssignments: bayAssignmentsInitialValue(request),
            remarks: request.remarks ?? "",
          }}
        />
      </BayHireFormPage>
    );
  }

  return (
    <BayHireFormPage
      titleKey="bay-hire.request-form-title"
      titleDefault="Bay Hire Request"
      submitError={submitError}
    >
      <BayHireRequestForm onSubmit={handleCreateSubmit} onCancel={goToGrid} />
    </BayHireFormPage>
  );
};

export default BayHireRequestFormPage;
